import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Output,
  EventEmitter,
} from '@angular/core';
import { DragulaService } from 'ng2-dragula';
import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';

import { WebsiteSocialLink } from '../../../portal.model';
import { selectWebsiteIdentity } from 'src/app/shared/store/_features/website/website.selector';
import { SocialLinksService } from '../shared-social-links.service';

@Component({
  selector: 'app-social-links-list',
  templateUrl: './social-links-list.component.html',
  styleUrls: ['./social-links-list.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SocialLinksListComponent implements OnInit, OnDestroy {
  readonly DRAGULA_BAG = 'social-links-bag';
  links: WebsiteSocialLink[] = [];
  private destroy$ = new Subject<void>();

  @Output() onStartProcessing = new EventEmitter<void>();
  @Output() onFinishProcessing = new EventEmitter<void>();

  modalOpen = false;
  editMode: 'add' | 'edit' = 'add';
  selectedLink: WebsiteSocialLink | null = null;
  disabled = false;

  constructor(
    private dragula: DragulaService,
    private store: Store,
    private linksService: SocialLinksService,
    private cdr: ChangeDetectorRef,
  ) {
    this.initDragula();
  }

  ngOnInit(): void {
    this.linksService.links$
      .pipe(takeUntil(this.destroy$))
      .subscribe((links) => {
        this.links = links;
        this.cdr.markForCheck();
      });

    this.store
      .select(selectWebsiteIdentity)
      .pipe(takeUntil(this.destroy$))
      .subscribe((identity) => {
        const socialLinks = (identity.social_links || []).map(
          (link, index) => ({ ...link, id: index + 1 }),
        );
        this.linksService.setInitialLinks(socialLinks);
      });

    this.subscribeToDragEvents();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.destroyDragula();
  }

  /** ────────────────────────────────
   *  UI Handlers
   *  ──────────────────────────────── */
  openAddModal(): void {
    this.editMode = 'add';
    this.selectedLink = null;
    this.modalOpen = true;
  }

  openEditModal(link: WebsiteSocialLink): void {
    this.editMode = 'edit';
    this.selectedLink = { ...link };
    this.modalOpen = true;
  }

  closeModal(): void {
    this.modalOpen = false;
    this.selectedLink = null;
  }

  async handleSave(link: WebsiteSocialLink): Promise<void> {
    if (this.editMode === 'edit') await this.linksService.updateLink(link);
    else await this.linksService.addLink(link);
    this.closeModal();
  }

  async confirmDelete(id: string): Promise<void> {
    await this.linksService.deleteLink(id);
  }

  async saveAll() {
    await this.linksService.saveAll();
  }

  resetChanges(): void {
    this.linksService.resetChanges();
  }

  /** ────────────────────────────────
   *  Drag & Drop
   *  ──────────────────────────────── */
  private initDragula(): void {
    this.destroyDragula();
    this.dragula.createGroup(this.DRAGULA_BAG, {
      direction: 'vertical',
      revertOnSpill: true,
      moves: (_el, _container, handle) =>
        !this.disabled && !!handle?.closest('.drag-handle'),
    });
  }

  private subscribeToDragEvents(): void {
    this.dragula
      .dropModel(this.DRAGULA_BAG)
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ targetModel }) => {
        this.linksService.reorderLinks(targetModel as WebsiteSocialLink[]);
      });
  }

  private destroyDragula(): void {
    if (this.dragula.find(this.DRAGULA_BAG)) {
      this.dragula.destroy(this.DRAGULA_BAG);
    }
  }

  trackByFn(_index: number, item: WebsiteSocialLink): string | number {
    return item.id;
  }
}
